import React from 'react';
import {
  CheckCircle,
  Clock,
  Lightning,
  Phone,
  Play,
  WebhooksLogo,
  WarningCircle,
} from '@phosphor-icons/react';

/**
 * ActivityFeed — lightweight dashboard widget showing recent agent activity.
 * Renders the last N events as a compact timeline list.
 *
 * Props:
 *  - events: Array<{ type, agent_name, timestamp, status, detail }>
 *  - maxItems: number (default 10)
 *  - loading: boolean
 */

const EVENT_CONFIG = {
  run_completed: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Run completed' },
  run_failed: { icon: WarningCircle, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Run failed' },
  run_started: { icon: Play, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Run started' },
  workflow_triggered: { icon: Lightning, color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Workflow triggered' },
  webhook_received: { icon: WebhooksLogo, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Webhook received' },
  voice_call: { icon: Phone, color: 'text-pink-400', bg: 'bg-pink-500/10', label: 'Voice call' },
};

const DEFAULT_EVENT = { icon: Lightning, color: 'text-[var(--text-muted)]', bg: 'bg-[var(--surface-3)]', label: 'Activity' };

function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export const ActivityFeed = ({ events = [], maxItems = 10, loading = false }) => {
  const displayEvents = events.slice(0, maxItems);

  if (loading) {
    return (
      <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-1)] overflow-hidden" data-testid="activity-feed">
        <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[var(--text-muted)]" />
            <span className="text-xs font-semibold text-white">Recent Activity</span>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-7 h-7 rounded-md bg-[var(--surface-3)]" />
              <div className="flex-1">
                <div className="h-3 w-32 bg-[var(--surface-3)] rounded mb-1" />
                <div className="h-2.5 w-20 bg-[var(--surface-4)] rounded" />
              </div>
              <div className="h-2.5 w-12 bg-[var(--surface-4)] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (displayEvents.length === 0) {
    return (
      <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-1)] overflow-hidden" data-testid="activity-feed">
        <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[var(--text-muted)]" />
            <span className="text-xs font-semibold text-white">Recent Activity</span>
          </div>
        </div>
        <div className="p-6 text-center">
          <Lightning size={20} className="text-[var(--text-muted)] mx-auto mb-2" />
          <p className="text-xs text-[var(--text-muted)]">No activity yet</p>
          <p className="text-[10px] text-[var(--text-muted)] opacity-60 mt-0.5">Run an agent to see activity here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-1)] overflow-hidden" data-testid="activity-feed">
      <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[var(--text-muted)]" />
            <span className="text-xs font-semibold text-white">Recent Activity</span>
          </div>
          <span className="text-[10px] text-[var(--text-muted)]">{displayEvents.length} events</span>
        </div>
      </div>

      <div className="divide-y divide-[var(--border-subtle)]">
        {displayEvents.map((event, idx) => {
          const config = EVENT_CONFIG[event.type] || DEFAULT_EVENT;
          const Icon = config.icon;
          return (
            <div
              key={event.id || idx}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface-2)] transition-colors"
              data-testid={`activity-event-${idx}`}
              style={{ transitionDuration: 'var(--motion-fast, 120ms)' }}
            >
              <div className={`w-7 h-7 rounded-md ${config.bg} flex items-center justify-center shrink-0`}>
                <Icon size={14} weight="duotone" className={config.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-medium truncate">
                  {event.agent_name || 'Agent'}
                </p>
                <p className="text-[10px] text-[var(--text-muted)] truncate">
                  {config.label}{event.detail ? ` — ${event.detail}` : ''}
                </p>
              </div>
              <span className="text-[10px] text-[var(--text-muted)] shrink-0 tabular-nums">
                {formatRelativeTime(event.timestamp)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityFeed;
